
import { LibraryDB } from './LibraryDB';
import { getApiUrl } from '../utils/urlHelper';

const BASE_URL = 'https://gutendex.com/books/';

export interface GutendexBook {
    id: number;
    title: string;
    authors: { name: string; birth_year?: number; death_year?: number }[];
    translators: { name: string; birth_year?: number; death_year?: number }[];
    subjects: string[];
    bookshelves: string[];
    languages: string[];
    copyright: boolean | null;
    media_type: string;
    formats: Record<string, string>;
    download_count: number;
}

export interface GutendexResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: GutendexBook[];
}

export interface SearchDiagnostic {
    query: string;
    source: 'memory' | 'local_storage' | 'network' | 'fallback_direct';
    durationMs: number;
    cfCacheStatus?: string | null;
    apiBaseUsed: string;
    contentType: string;
    status: number;
    timestamp: number;
}

let lastSearchDiagnostic: SearchDiagnostic | null = null;

export function getLastSearchDiagnostic(): SearchDiagnostic | null {
    return lastSearchDiagnostic;
}

export const GutendexService = {
    // In-Memory Cache (Simple LRU-ish, cleared on flush)
    _cache: new Map<string, { data: GutendexResponse, ts: number }>(),

    async search(query: string, lang: string = 'en', page: number = 1, sort?: string, topic?: string): Promise<GutendexResponse> {
        const start = performance.now();
        // Cache Key
        const cacheKey = `gutendex_q=${query}_l=${lang}_p=${page}_s=${sort || 'def'}_t=${topic || 'none'}`;

        // 1. Check In-Memory (1h)
        const mem = this._cache.get(cacheKey);
        if (mem && (Date.now() - mem.ts < 3600000)) {
            const duration = performance.now() - start;
            lastSearchDiagnostic = {
                query,
                source: 'memory',
                durationMs: duration,
                apiBaseUsed: 'none',
                contentType: 'application/json',
                status: 200,
                timestamp: Date.now()
            };
            return mem.data;
        }

        // 2. Check LocalStorage (6h)
        try {
            const lsItem = localStorage.getItem(cacheKey);
            if (lsItem) {
                const parsed = JSON.parse(lsItem);
                if (Date.now() - parsed.ts < 21600000) { // 6 hours
                    // Populate memory for faster next access
                    this._cache.set(cacheKey, parsed);
                    const duration = performance.now() - start;
                    lastSearchDiagnostic = {
                        query,
                        source: 'local_storage',
                        durationMs: duration,
                        apiBaseUsed: 'none',
                        contentType: 'application/json',
                        status: 200,
                        timestamp: Date.now()
                    };
                    return parsed.data;
                }
            }
        } catch (e) { /* Ignore LS errors */ }

        // 3. Network Fetch
        const apiSearchUrl = getApiUrl('/gutendex/search');
        let resolvedUrl: URL;
        if (apiSearchUrl.startsWith('http://') || apiSearchUrl.startsWith('https://')) {
            resolvedUrl = new URL(apiSearchUrl);
        } else {
            resolvedUrl = new URL(apiSearchUrl, window.location.origin);
        }

        if (query) resolvedUrl.searchParams.set('q', query);
        if (topic) resolvedUrl.searchParams.set('topic', topic);
        if (lang !== 'all') {
            resolvedUrl.searchParams.set('lang', lang);
        }
        if (page > 1) {
            resolvedUrl.searchParams.set('page', page.toString());
        }
        if (sort) {
            resolvedUrl.searchParams.set('sort', sort);
        }

        let res: Response;
        let source: 'network' | 'fallback_direct' = 'network';
        let apiBaseUsed = resolvedUrl.origin;

        try {
            res = await fetch(resolvedUrl.toString());
        } catch (e: any) {
            console.warn("[GutendexService] Relative/Proxy fetch failed, falling back to direct Gutendex fetch", e);
            const directUrl = new URL(BASE_URL);
            if (query) directUrl.searchParams.set('search', query);
            if (topic) directUrl.searchParams.set('topic', topic);
            if (lang !== 'all') {
                directUrl.searchParams.set('languages', lang);
            }
            if (page > 1) {
                directUrl.searchParams.set('page', page.toString());
            }
            if (sort) {
                directUrl.searchParams.set('sort', sort);
            }
            source = 'fallback_direct';
            apiBaseUsed = directUrl.origin;
            res = await fetch(directUrl.toString());
            if (!res.ok) throw new Error(`Gutendex API error: ${res.statusText}`);
        }

        const duration = performance.now() - start;
        console.debug(`[GutendexService] Search request took ${duration.toFixed(1)}ms`);

        const contentType = res.headers.get('content-type') || '';
        const cfCacheStatus = res.headers.get('cf-cache-status');

        // Record diagnostic immediately
        lastSearchDiagnostic = {
            query,
            source,
            durationMs: duration,
            cfCacheStatus,
            apiBaseUsed,
            contentType,
            status: res.status,
            timestamp: Date.now()
        };

        const bodyText = await res.text();

        if (contentType.includes('text/html') || bodyText.toLowerCase().includes('<!doctype html>')) {
            throw new Error("Route misconfiguration: The API returned HTML instead of JSON. The `/api/*` routes are not configured correctly on this domain.");
        }

        if (!res.ok) {
            throw new Error(`Search failed: HTTP ${res.status} ${res.statusText}`);
        }

        let data: GutendexResponse;
        try {
            data = JSON.parse(bodyText);
        } catch (e) {
            throw new Error("Route misconfiguration: API response is not valid JSON.");
        }

        // 4. Update Caches
        const cacheObj = { data, ts: Date.now() };
        this._cache.set(cacheKey, cacheObj);

        try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheObj));
        } catch (e) {
            // LS Full, try clearing old items? For now just skip.
        }

        // Legacy DB Cache (Keep for potential offline use of simple queries)
        if (!sort && !topic) {
            await LibraryDB.setCache(query, lang, page, data);
        }

        return data;
    },

    // Helper to pick best format candidates
    getDownloadCandidates(book: GutendexBook): { mime: string, url: string }[] {
        const candidates: { mime: string, url: string }[] = [];
        const formats = book.formats;
        const id = book.id;

        // Priority 1: Gutenberg CDN/Cache endpoint (pre-generated clean plain text)
        // Since the direct format links from Gutendex are notoriously brittle or rate-limited,
        // we prioritize the Gutenberg cache CDN endpoint.
        candidates.push({ mime: 'text/plain; charset=utf-8', url: `https://www.gutenberg.org/cache/epub/${id}/pg${id}.txt` });

        // Priority 2: Format links returned from the API
        if (formats['text/plain; charset=utf-8']) {
            candidates.push({ mime: 'text/plain; charset=utf-8', url: formats['text/plain; charset=utf-8'] });
        }
        if (formats['text/plain']) {
            candidates.push({ mime: 'text/plain', url: formats['text/plain'] });
        }
        if (formats['text/plain; charset=us-ascii']) {
            candidates.push({ mime: 'text/plain; charset=us-ascii', url: formats['text/plain; charset=us-ascii'] });
        }

        // Priority 3: HTML (good structure)
        if (formats['text/html']) {
            candidates.push({ mime: 'text/html', url: formats['text/html'] });
        }

        // Priority 4: EPUB
        if (formats['application/epub+zip']) {
            candidates.push({ mime: 'application/epub+zip', url: formats['application/epub+zip'] });
        }

        // Fallback: Older files structure
        candidates.push({ mime: 'fallback/files-0-txt', url: `https://www.gutenberg.org/files/${id}/${id}-0.txt` });

        return candidates;
    }
};
